import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useAuth0 } from "@auth0/auth0-react";
import UserList from "../Components/User List/UserList";
import axios from 'axios';
import ConversationView from "../Components/ConversationView/ConversationView";
import Chatbot from "../Components/Dashboard Panels/Chatbot/chatbot";
import ChatbotIcon from "../Images/chatbot.svg";
import CaseDashboard from "../Components/Dashboard Panels/CaseDashboard/CaseDashboard";
import CaseFile from "../Components/Dashboard Panels/CaseFile/CaseFile";
import DocumentCenter from "../Components/Dashboard Panels/DocumentCenter/DocumentCenter";
import UserFormsPanel from "../Components/Dashboard Panels/UserFormsPanel";
import ClientProfilePanel from "../Components/Dashboard Panels/ClientProfilePanel/ClientProfilePanel";
import ConversationObserver from "../Components/Dashboard Panels/Observer/observer";
import ObserverIcon from "../Images/observer.svg";
import Macros from "../Components/Macros";
import MacrosIcon from "../Images/macros.svg";
import HubAssistantIcon from "../Images/hubAssistant.svg";
import "../Components/Dashboard Panels/Observer/observer.css";
import { useParams } from 'react-router-dom';

// Import Workspace Assistant components
import { 
  WorkspaceAssistantPanel, 
  WorkspaceAssistantProvider, 
  useWorkspaceAssistant 
} from "../Components/WorkspaceAssistant";

const PageWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%);
  box-sizing: border-box;
  overflow: hidden;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  gap: 0;
`;

const LeftPanel = styled.div`
  flex: 3;
  background: linear-gradient(180deg, rgba(30, 35, 50, 0.95) 0%, rgba(20, 25, 40, 0.98) 100%);
  border-radius: 12px;
  margin: 10px 10px 10px 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(100, 150, 200, 0.15);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const RightPanel = styled.div`
  flex: 1;
  background: linear-gradient(180deg, rgba(30, 35, 50, 0.95) 0%, rgba(20, 25, 40, 0.98) 100%);
  border-radius: 12px;
  margin: 10px 10px 10px 0;
  color: #FFFFFF;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(100, 150, 200, 0.15);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const TabsContainer = styled.div`
  display: flex;
  background: linear-gradient(90deg, rgba(25, 30, 45, 0.98) 0%, rgba(35, 45, 65, 0.95) 100%);
  border-bottom: 1px solid rgba(100, 150, 200, 0.2);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  width: 100%;
  height: 44px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none; /* Firefox */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Edge */
  }
`;

const TabIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  
  svg, img {
    width: 16px;
    height: 16px;
  }
`;

const TabText = styled.span`
  white-space: nowrap;
`;

const Tab = styled.div`
  padding: 0 14px;
  cursor: pointer;
  color: ${props => props.$isActive ? 'rgba(200, 220, 255, 0.95)' : 'rgba(180, 195, 220, 0.65)'};
  border-bottom: 2px solid ${props => props.$isActive ? 'rgba(100, 160, 230, 0.9)' : 'transparent'};
  transition: all 0.3s ease;
  font-weight: ${props => props.$isActive ? '500' : 'normal'};
  display: flex;
  align-items: center;
  position: relative;
  gap: 6px;
  font-size: 13px;
  margin: 0 4px;
  border-radius: 6px 6px 0 0;
  background: ${props => props.$isActive ? 'linear-gradient(180deg, rgba(60, 80, 120, 0.4) 0%, rgba(40, 55, 85, 0.3) 100%)' : 'transparent'};
  flex: 0 0 auto;
  font-size: 14px;
  height: 100%;
  
  &:hover {
    color: rgba(200, 220, 255, 0.95);
    background: linear-gradient(180deg, rgba(60, 80, 120, 0.3) 0%, rgba(40, 55, 85, 0.2) 100%);
  }
  
  svg {
    width: 16px;
    height: 16px;
    opacity: ${props => props.$isActive ? '1' : '0.7'};
    transition: all 0.3s ease;
    color: ${props => props.$isActive ? 'rgba(100, 180, 255, 0.95)' : 'rgba(180, 195, 220, 0.8)'};
  }
  
  img {
    width: 16px;
    height: 16px;
    opacity: ${props => props.$isActive ? '1' : '0.7'};
    transition: all 0.3s ease;
    filter: ${props => props.$isActive ? 'invert(48%) sepia(85%) saturate(2476%) hue-rotate(190deg) brightness(100%) contrast(90%)' : 'brightness(1.5) invert(1)'};
  }
  
  &:hover svg, &:hover img {
    opacity: 1;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${props => props.$isActive ? 'linear-gradient(90deg, rgba(80, 140, 220, 0.9), rgba(120, 180, 255, 0.95))' : 'transparent'};
    border-radius: 4px;
    box-shadow: ${props => props.$isActive ? '0 0 10px rgba(80, 140, 220, 0.5)' : 'none'};
    animation: ${props => props.$isActive ? 'pulse 2s infinite' : 'none'};
  }
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 5px rgba(80, 140, 220, 0.4);
    }
    50% {
      box-shadow: 0 0 12px rgba(80, 140, 220, 0.6);
    }
    100% {
      box-shadow: 0 0 5px rgba(80, 140, 220, 0.4);
    }
  }
`;

const TabContent = styled.div`
  color: #FFFFFF;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
`;

const TabPanel = styled.div`
  display: ${props => props.$isActive ? 'flex' : 'none'};
  flex-direction: column;
  height: 100%;
  animation: ${props => props.$isActive ? 'fadeIn 0.3s ease' : 'none'};
  
  @keyframes fadeIn {
    from {
      opacity: 0.7;
      transform: translateY(5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const PanelHeader = styled.div`
  padding: 10px;
  display: flex;
  align-items: center;
  background: linear-gradient(90deg, rgba(25, 30, 45, 0.98) 0%, rgba(35, 45, 65, 0.95) 100%);
  height: 42px;
  border-bottom: 1px solid rgba(100, 150, 200, 0.2);
  position: relative;
  
  img {
    width: 20px;
    height: 20px;
    margin-right: 8px;
    filter: invert(1);
  }
  
  span {
    font-weight: 500;
    color: rgba(200, 220, 255, 0.95);
  }
`;

const PanelContent = styled.div`
  flex: 1;
  background: linear-gradient(180deg, rgba(25, 30, 45, 0.6) 0%, rgba(20, 25, 40, 0.8) 100%);
  overflow: hidden;
`;


const EmptyStateMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  padding: 40px;
  
  svg {
    width: 60px;
    height: 60px;
    margin-bottom: 20px;
    opacity: 0.5;
  }
  
  h3 {
    font-size: 18px;
    margin-bottom: 10px;
    font-weight: normal;
  }
  
  p {
    font-size: 14px;
    max-width: 500px;
  }
`;

// Helper function to get right panel config based on active tab
const getRightPanelConfig = (activeTab) => {
  switch (activeTab) {
    case 'caseDashboard':
      return {
        icon: HubAssistantIcon,
        title: 'Dashboard Assistant',
        mode: 'dashboard'
      };
    case 'caseFile':
      return {
        icon: HubAssistantIcon,
        title: 'Dashboard Assistant',
        mode: 'dashboard'
      };
    case 'documentCenter':
      return {
        icon: HubAssistantIcon,
        title: 'Dashboard Assistant',
        mode: 'dashboard'
      };
    case 'clientData':
      return {
        icon: HubAssistantIcon,
        title: 'Dashboard Assistant',
        mode: 'dashboard'
      };
    case 'conversation':
      return {
        icon: ObserverIcon,
        title: 'Observer',
        mode: 'observer'
      };
    case 'applicationReview':
      return {
        icon: HubAssistantIcon,
        title: 'Workspace Assistant',
        mode: 'workspace'
      };
    case 'radar':
      return {
        icon: HubAssistantIcon,
        title: 'Workspace Assistant',
        mode: 'workspace'
      };
    case 'history':
      return {
        icon: MacrosIcon,
        title: 'Macros',
        mode: 'macros'
      };
    default:
      return {
        icon: HubAssistantIcon,
        title: 'Workspace Assistant',
        mode: 'workspace'
      };
  }
};

// Inner component that uses the workspace assistant context
const WorkspacePageContent = () => {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [activeTab, setActiveTab] = useState('caseFile'); // Default to Overview
  const { threadId } = useParams();
  const fetchTimerRef = useRef(null);
  const loadingTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    };
  }, []);
  
  // Incomplete leads state
  const [incompleteConversations, setIncompleteConversations] = useState([]);
  const [incompleteLoading, setIncompleteLoading] = useState(false);

  // Get workspace assistant context
  const { updateWorkspaceContext, setSelectedUser: setAssistantSelectedUser, setActiveTab: setAssistantActiveTab } = useWorkspaceAssistant();

  // Update workspace assistant context when tab changes
  useEffect(() => {
    setAssistantActiveTab(activeTab);
    updateWorkspaceContext({
      activeTab,
      rightPanelMode: getRightPanelConfig(activeTab).mode
    });
  }, [activeTab, setAssistantActiveTab, updateWorkspaceContext]);

  // Update workspace assistant context when conversation changes
  useEffect(() => {
    if (selectedConversation) {
      const threadId = selectedConversation._id || selectedConversation.threadId;
      setAssistantSelectedUser(selectedConversation, threadId);
      updateWorkspaceContext({
        selectedUser: selectedConversation,
        threadId
      });
    }
  }, [selectedConversation, setAssistantSelectedUser, updateWorkspaceContext]);

  const refreshConversations = useCallback(async () => {
    try {
      if (isAuthenticated && user) {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${process.env.REACT_APP_SERVER}userConversation`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.status === 200) {
          setConversations(response.data);
        }
      }
    } catch (error) {
      console.error('Error refreshing conversations:', error);
    }
  }, [isAuthenticated, user, getAccessTokenSilently]);

  // Fetch conversations to initialize the user list
  useEffect(() => {
    let cancelled = false;

    const fetchConversations = async () => {
      try {
        if (isAuthenticated && user) {
          setIsLoadingData(true);
          const token = await getAccessTokenSilently();

          const response = await axios.get(`${process.env.REACT_APP_SERVER}userConversation`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });

          if (cancelled) return;

          if (response.status === 200) {
            setConversations(response.data);

            if (threadId) {
              setIsLoadingData(true)
              fetchTimerRef.current = setTimeout(async () => {
                if (cancelled) return;
                const convo = response.data.find(x =>
                  x.threadId === threadId ||
                  x._id === threadId ||
                  x.id === threadId
                );

                const res = await axios.get(`${process.env.REACT_APP_SERVER}conversations/${threadId}`, {
                  headers: {
                    Authorization: `Bearer ${token}`
                  }
                });

                if (cancelled) return;

                if (res.status === 200) {
                  const fallbackUserId = res.data?.users?.find(u =>
                    u?.type !== 'expert' && u?.type !== 'gpt'
                  )?.id;
                  handleConversationSelect(res.data, convo?.id || fallbackUserId || null)
                } else {
                  throw new Error('No conversation found')
                }

              }, 1)
              setIsLoadingData(false)
            }

          } else {
            console.error('Failed to fetch conversations', response.statusText);
          }
          if (!cancelled) setIsLoadingData(false);
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
        if (!cancelled) setIsLoadingData(false);
      }
    };

    fetchConversations();

    return () => {
      cancelled = true;
      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current);
        fetchTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, getAccessTokenSilently]);

  // Fetch incomplete conversations (widget sessions that started but didn't finish)
  const fetchIncompleteConversations = useCallback(async (includeHidden = false, inactiveMinutes = 60) => {
    try {
      if (!user?.sub) return;
      
      setIncompleteLoading(true);
      const token = await getAccessTokenSilently();
      
      // The expertId is the user.sub (Auth0 user ID) - must be URL encoded
      const expertId = encodeURIComponent(user.sub);
      let serverUrl = process.env.REACT_APP_SERVER;
      
      // Ensure the URL ends with a slash
      if (!serverUrl.endsWith('/')) {
        serverUrl += '/';
      }
      
      const requestUrl = `${serverUrl}funnel/incomplete/${expertId}?includeHidden=${includeHidden}&inactiveMinutes=${inactiveMinutes}`;
      console.log('[Incomplete Leads] Fetching from:', requestUrl);
      
      const response = await axios.get(requestUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.status === 200 && response.data?.sessions) {
        setIncompleteConversations(response.data.sessions);
      }
    } catch (error) {
      console.error('Error fetching incomplete conversations:', error);
    } finally {
      setIncompleteLoading(false);
    }
  }, [user?.sub, getAccessTokenSilently]);

  // Hide an incomplete session
  const handleHideSession = useCallback(async (sessionId) => {
    try {
      const token = await getAccessTokenSilently();
      let serverUrl = process.env.REACT_APP_SERVER;
      
      // Ensure the URL ends with a slash
      if (!serverUrl.endsWith('/')) {
        serverUrl += '/';
      }
      
      const response = await axios.put(
        `${serverUrl}funnel/session/${encodeURIComponent(sessionId)}/hide`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('[Incomplete Leads] Session hidden successfully:', sessionId, response.data);
      
      // Remove the hidden session from local state entirely (don't just mark it)
      // This ensures it won't reappear even if there's a re-render
      setIncompleteConversations(prev => 
        prev.filter(session => session.sessionId !== sessionId)
      );
    } catch (error) {
      console.error('Error hiding session:', error);
      // Re-throw so the caller (UserList) can show an error toast
      throw error;
    }
  }, [getAccessTokenSilently]);

  // Unhide an incomplete session
  const handleUnhideSession = useCallback(async (sessionId) => {
    try {
      const token = await getAccessTokenSilently();
      let serverUrl = process.env.REACT_APP_SERVER;
      
      // Ensure the URL ends with a slash
      if (!serverUrl.endsWith('/')) {
        serverUrl += '/';
      }
      
      await axios.put(
        `${serverUrl}funnel/session/${encodeURIComponent(sessionId)}/unhide`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('[Incomplete Leads] Session unhidden successfully:', sessionId);
      
      // Update local state
      setIncompleteConversations(prev => 
        prev.map(session => 
          session.sessionId === sessionId 
            ? { ...session, isHidden: false, hiddenAt: null }
            : session
        )
      );
    } catch (error) {
      console.error('Error unhiding session:', error);
      // Re-throw so the caller (UserList) can show an error toast
      throw error;
    }
  }, [getAccessTokenSilently]);

  // Handle adding a new lead manually
  const handleAddLead = useCallback(async (leadData) => {
    try {
      if (!user?.sub) return { success: false };
      
      const token = await getAccessTokenSilently();
      let serverUrl = process.env.REACT_APP_SERVER;
      
      // Ensure the URL ends with a slash
      if (!serverUrl.endsWith('/')) {
        serverUrl += '/';
      }
      
      const response = await axios.post(
        `${serverUrl}funnel/lead/manual`,
        {
          expertId: user.sub,
          userName: leadData.name,
          userEmail: leadData.email,
          userPhone: leadData.phone || '',
          notes: leadData.notes || '',
          language: 'en' // Could be dynamic based on user preference
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.status === 200 || response.status === 201) {
        console.log('[Manual Lead] Lead added successfully:', response.data);
        
        // Refresh the incomplete conversations list to show the new lead
        fetchIncompleteConversations(false, 60);
        
        // Return the full response including the signup link
        return {
          success: true,
          signupLink: response.data.signupLink,
          sessionId: response.data.sessionId,
          conversationId: response.data.conversationId
        };
      }
      return { success: false };
    } catch (error) {
      console.error('Error adding manual lead:', error);
      return { success: false, error: error.message };
    }
  }, [user?.sub, getAccessTokenSilently, fetchIncompleteConversations]);

  const handleGenerateSignupLink = useCallback(async (sessionId) => {
    try {
      const token = await getAccessTokenSilently();
      let serverUrl = process.env.REACT_APP_SERVER;
      if (!serverUrl.endsWith('/')) serverUrl += '/';
      
      const response = await axios.post(
        `${serverUrl}funnel/session/${encodeURIComponent(sessionId)}/generate-link`,
        { language: 'en' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error generating signup link:', error);
      return { success: false, error: error.message };
    }
  }, [getAccessTokenSilently]);

  const handleConvertToLead = useCallback(async (sessionId) => {
    try {
      const token = await getAccessTokenSilently();
      let serverUrl = process.env.REACT_APP_SERVER;
      if (!serverUrl.endsWith('/')) serverUrl += '/';
      
      const response = await axios.post(
        `${serverUrl}funnel/session/${encodeURIComponent(sessionId)}/convert-to-lead`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.success) {
        setIncompleteConversations(prev => 
          prev.filter(session => session.sessionId !== sessionId)
        );
        fetchIncompleteConversations(false, 60);
        refreshConversations();
      }
      
      return response.data;
    } catch (error) {
      console.error('Error converting to lead:', error);
      return { success: false, error: error.message };
    }
  }, [getAccessTokenSilently, fetchIncompleteConversations, refreshConversations]);

  const handleGenerateSignupLinkForLead = useCallback(async (threadId) => {
    try {
      const token = await getAccessTokenSilently();
      let serverUrl = process.env.REACT_APP_SERVER;
      if (!serverUrl.endsWith('/')) serverUrl += '/';
      
      const response = await axios.post(
        `${serverUrl}funnel/session/by-thread/${encodeURIComponent(threadId)}/generate-link`,
        { language: 'en' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error generating signup link for lead:', error);
      return { success: false, error: error.message };
    }
  }, [getAccessTokenSilently]);

  const handleSendSignupEmail = useCallback(async ({ email, userName, expertName, signupLink }) => {
    try {
      const token = await getAccessTokenSilently();
      let serverUrl = process.env.REACT_APP_SERVER;
      if (!serverUrl.endsWith('/')) serverUrl += '/';
      
      const response = await axios.post(
        `${serverUrl}funnel/send-signup-email`,
        { email, userName, expertName, signupLink },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error sending signup email:', error);
      return { success: false, error: error.message };
    }
  }, [getAccessTokenSilently]);

  // Handle clicking on an incomplete conversation - fetch and display the actual conversation
  // If the session has previousConversationIds (user reconnected), merge all messages together
  const handleIncompleteConversationSelect = useCallback(async (session) => {
    try {
      if (!session.conversationId) {
        console.warn('[Incomplete] No conversationId available for this session');
        return;
      }
      
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch current conversation
      const allConvoIds = [
        ...(session.previousConversationIds || []),
        session.conversationId
      ];
      
      console.log('[Incomplete] Fetching conversations:', allConvoIds);
      
      const responses = await Promise.all(
        allConvoIds.map(id =>
          axios.get(`${process.env.REACT_APP_SERVER}conversations/${id}`, { headers })
            .catch(() => null)
        )
      );
      
      const validConvos = responses
        .filter(r => r && r.status === 200 && r.data)
        .map(r => r.data);
      
      if (validConvos.length === 0) {
        console.warn('[Incomplete] No conversation data found');
        return;
      }
      
      // Use the conversation with the most messages as the primary (preserves metadata)
      const primary = validConvos.reduce((best, c) =>
        (c.messages?.length || 0) > (best.messages?.length || 0) ? c : best
      );
      
      // If there are multiple conversations, merge all messages chronologically
      if (validConvos.length > 1) {
        const allMessages = validConvos.flatMap(c => c.messages || []);
        allMessages.sort((a, b) => new Date(a.time) - new Date(b.time));
        primary.messages = allMessages;
        console.log(`[Incomplete] Merged ${validConvos.length} conversations (${allMessages.length} total messages)`);
      }
      
      // Find a user in the conversation (filter out GPT and current user)
      const filteredUsers = primary.users ? primary.users.filter((u) => {
        return u && u.id && u.id !== user?.sub && u.userName !== "GPT";
      }) : [];
      
      const selectedUser = filteredUsers[0] || primary.users?.[0] || { id: 'unknown', userName: 'Unknown User' };
      
      handleConversationSelect(primary, selectedUser.id);
    } catch (error) {
      console.error('[Incomplete] Error fetching conversation:', error);
    }
  }, [getAccessTokenSilently, user?.sub]);

  const handleConversationSelect = async (conversation, userId) => {
    // Find the original conversation from the list to get userIcon
    const conversationThreadId = conversation?._id || conversation?.threadId;
    const originalConv = conversations.find(c => 
      c.threadId === conversationThreadId || c._id === conversationThreadId
    );
    
    // Merge userIcon from original conversation if available.
    // The `shared` / `sharedFromName` flags only exist on the leads-list items
    // (the leads API marks conversations shared with this expert as a
    // subcontractor); the single-conversation fetch that reaches this handler
    // does not carry them, so pull them from the matching list item so the
    // selected conversation knows it is a shared (subcontracted) case.
    const enrichedConversation = {
      ...conversation,
      userIcon: conversation?.userIcon || originalConv?.userIcon || null,
      shared: conversation?.shared ?? originalConv?.shared ?? false,
      sharedFromName: conversation?.sharedFromName ?? originalConv?.sharedFromName ?? null
    };
    
    setSelectedConversation(enrichedConversation);
    setSelectedUserId(userId);
    
    // Check if case analysis exists to determine which tab to open
    // 404 is expected for users without analysis - this is NOT an error, just means analysis needs to run
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${process.env.REACT_APP_SERVER}analysis/case-options/${conversationThreadId}`, {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: (status) => status === 200 || status === 404 // Don't throw on 404
      });
      
      // If analysis exists (200 with data), open Overview tab
      if (response.status === 200 && response.data?.analysis) {
        setActiveTab('caseFile'); // Overview
      } else {
        // No analysis yet (404 or empty response), open Case Dashboard to run analysis
        setActiveTab('caseDashboard');
      }
    } catch (err) {
      // For unexpected errors, default to Case Dashboard (safest option)
      console.warn('[WorkspacePage] Could not check analysis status, defaulting to Case Dashboard');
      setActiveTab('caseDashboard');
    }
  };

  const handleLoadingData = () => {
    setIsLoadingData(true);
    if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    loadingTimerRef.current = setTimeout(() => {
      setIsLoadingData(false);
    }, 1000);
  };

  // Function to update conversation (for socket updates)
  const updateConversationViaSocket = (updatedConversation) => {
    console.log('Updating conversation via socket:', updatedConversation);
    setSelectedConversation(updatedConversation);
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Render functions for tab content
  const renderConversationTab = () => {
    if (!selectedConversation) {
      return (
        <EmptyStateMessage>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          <h3>No Conversation Selected</h3>
          <p>Select a user from the list to start or continue a conversation.</p>
        </EmptyStateMessage>
      );
    }

    return (
      <ConversationView
        conversation={selectedConversation}
        userId={selectedUserId}
        setConversation={updateConversationViaSocket}
      />
    );
  };

  const renderCaseDashboardTab = () => {
    if (!selectedConversation) {
      return (
        <EmptyStateMessage>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <h3>No Case Dashboard Available</h3>
          <p>Select a user from the list to view case summary, options, and readiness analysis.</p>
        </EmptyStateMessage>
      );
    }

    const threadId = selectedConversation._id || selectedConversation.threadId;

    return (
      <PanelContent style={{ height: '100%' }}>
        <CaseDashboard
          key={`case-dashboard-${threadId}`}
          threadId={threadId}
          conversation={selectedConversation}
        />
      </PanelContent>
    );
  };

  const renderCaseFileTab = () => {
    if (!selectedConversation) {
      return (
        <EmptyStateMessage>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
          </svg>
          <h3>No Case Selected</h3>
          <p>Select a user from the list to view their overview.</p>
        </EmptyStateMessage>
      );
    }

    const threadId = selectedConversation._id || selectedConversation.threadId;

    return (
      <PanelContent style={{ height: '100%' }}>
        <CaseFile
          key={`case-file-${threadId}`}
          threadId={threadId}
          conversation={selectedConversation}
        />
      </PanelContent>
    );
  };

  const renderHistoryTab = () => {
    if (!selectedConversation) {
      return (
        <EmptyStateMessage>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 8v4l3 3"></path>
            <circle cx="12" cy="12" r="9"></circle>
          </svg>
          <h3>No Conversation Selected</h3>
          <p>Select a user from the list to use the chatbot.</p>
        </EmptyStateMessage>
      );
    }

    const threadId = selectedConversation._id || selectedConversation.threadId;

    return (
      <PanelContent style={{ height: '100%' }}>
        <Chatbot
          key={`chatbot-${threadId}`}
          threadId={threadId}
        />
      </PanelContent>
    );
  };

  const renderVideoCallTab = () => {
    if (!selectedConversation) {
      return (
        <EmptyStateMessage>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 7l-7 5 7 5V7z"></path>
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
          </svg>
          <h3>No Conversation Selected</h3>
          <p>Select a user from the list to start a video call.</p>
        </EmptyStateMessage>
      );
    }

    return (
      <PanelContent style={{ height: '100%' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'rgba(255, 255, 255, 0.6)',
          textAlign: 'center',
          padding: '40px'
        }}>
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '60px', height: '60px', marginBottom: '20px', opacity: '0.5' }}>
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <path d="M9 9h6v6H9z"></path>
              <path d="M9 21v-6"></path>
              <path d="M21 9h-6"></path>
            </svg>
            <h3 style={{ fontSize: '18px', marginBottom: '10px', fontWeight: 'normal' }}>Video Call Coming Soon</h3>
            <p style={{ fontSize: '14px', maxWidth: '400px', margin: '0 auto' }}>
              Video call functionality will be available here in later date.
            </p>
          </div>
        </div>
      </PanelContent>
    );
  };

  const renderWhiteBoardTab = () => {
    if (!selectedConversation) {
      return (
        <EmptyStateMessage>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <path d="M9 9h6v6H9z"></path>
          </svg>
          <h3>No Conversation Selected</h3>
          <p>Select a user from the list to access the whiteboard.</p>
        </EmptyStateMessage>
      );
    }

    return (
      <PanelContent style={{ height: '100%' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'rgba(255, 255, 255, 0.6)',
          textAlign: 'center',
          padding: '40px'
        }}>
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '60px', height: '60px', marginBottom: '20px', opacity: '0.5' }}>
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <path d="M9 9h6v6H9z"></path>
              <path d="M9 21v-6"></path>
              <path d="M21 9h-6"></path>
            </svg>
            <h3 style={{ fontSize: '18px', marginBottom: '10px', fontWeight: 'normal' }}>WhiteBoard Coming Soon</h3>
            <p style={{ fontSize: '14px', maxWidth: '400px', margin: '0 auto' }}>
              Interactive whiteboard functionality will be available here for collaborative sessions with users.
            </p>
          </div>
        </div>
      </PanelContent>
    );
  };

  // Render right panel content based on active tab
  const renderRightPanelContent = () => {
    const config = getRightPanelConfig(activeTab);
    
    switch (config.mode) {
      case 'dashboard':
        // Dashboard Assistant for Case Dashboard tab
        return <WorkspaceAssistantPanel mode="dashboard" />;
        
      case 'observer':
        // Observer for Conversation tab
        if (selectedConversation) {
          return (
            <ConversationObserver
              key={`observer-${selectedConversation.observerId || selectedConversation._id}`}
              observerId={selectedConversation.observerId}
            />
          );
        }
        return (
          <EmptyStateMessage>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
            <h3>No Observation Available</h3>
            <p>Select a conversation to see AI observations and insights.</p>
          </EmptyStateMessage>
        );
        
      case 'workspace':
        // Workspace Assistant for Application Review tab
        return <WorkspaceAssistantPanel mode="workspace" />;
        
      case 'macros':
        // Macros for Chatbot Assistant tab
        return <Macros />;
        
      default:
        return null;
    }
  };

  if (isLoading || !isAuthenticated) {
    return (
      <div style={{
        color: 'white',
        backgroundColor: '#0f1419',
        padding: '40px',
        minHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <h1 style={{ color: 'white', marginBottom: '20px' }}>Loading Workspace...</h1>
        <div style={{
          width: '100px',
          height: '100px',
          border: '5px solid rgba(120, 130, 140, 0.3)',
          borderTop: '5px solid rgba(120, 130, 140, 0.7)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const rightPanelConfig = getRightPanelConfig(activeTab);

  return (
    <PageWrapper>
      <div style={{ width: '400px', height: '100%', marginLeft: '10px' }}>
        <UserList
          conversationsInit={conversations}
          clientId={user?.sub}
          onConversationSelect={handleConversationSelect}
          loadingData={handleLoadingData}
          threadIdParam={selectedConversation?._id || selectedConversation?.threadId || threadId || ''}
          selectedUserOnNavigation={selectedConversation?.users?.find(u => u.id === selectedUserId) || null}
          // Incomplete leads props
          incompleteConversations={incompleteConversations}
          incompleteLoading={incompleteLoading}
          onHideSession={handleHideSession}
          onUnhideSession={handleUnhideSession}
          onFetchIncomplete={fetchIncompleteConversations}
          onIncompleteConversationSelect={handleIncompleteConversationSelect}
          // Manual lead creation
          onAddLead={handleAddLead}
          // Refresh the main Leads list (used after a ZIP import completes)
          onRefreshConversations={refreshConversations}
          // Incomplete session actions
          onGenerateSignupLink={handleGenerateSignupLink}
          onConvertToLead={handleConvertToLead}
          onGenerateSignupLinkForLead={handleGenerateSignupLinkForLead}
          onSendSignupEmail={handleSendSignupEmail}
        />
      </div>

      <MainContent>
        <LeftPanel>
          <TabsContainer>
            {/* Tab 1: Overview (Main Tab) */}
            <Tab
              $isActive={activeTab === 'caseFile'}
              onClick={() => handleTabChange('caseFile')}
            >
              <TabIcon>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                </svg>
              </TabIcon>
              <TabText>Overview</TabText>
            </Tab>

            {/* Tab 2: Case Dashboard */}
            <Tab
              $isActive={activeTab === 'caseDashboard'}
              onClick={() => handleTabChange('caseDashboard')}
            >
              <TabIcon>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </TabIcon>
              <TabText>Case Dashboard</TabText>
            </Tab>

            {/* Tab 3: Forms */}
            <Tab
              $isActive={activeTab === 'documentCenter'}
              onClick={() => handleTabChange('documentCenter')}
            >
              <TabIcon>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <line x1="10" y1="9" x2="8" y2="9"/>
                </svg>
              </TabIcon>
              <TabText>Forms</TabText>
            </Tab>

            {/* Tab: Client Data (all DB info about the user) */}
            <Tab
              $isActive={activeTab === 'clientData'}
              onClick={() => handleTabChange('clientData')}
            >
              <TabIcon>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </TabIcon>
              <TabText>Client Data</TabText>
            </Tab>

            {/* Tab 4: Conversation */}
            <Tab
              $isActive={activeTab === 'conversation'}
              onClick={() => handleTabChange('conversation')}
            >
              <TabIcon>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              </TabIcon>
              <TabText>Conversation</TabText>
            </Tab>

            {/* Tab: Chatbot Assistant */}
            <Tab
              $isActive={activeTab === 'history'}
              onClick={() => handleTabChange('history')}
            >
              <TabIcon>
                <img src={ChatbotIcon} alt="Chatbot" />
              </TabIcon>
              <TabText>Chatbot Assistant</TabText>
            </Tab>

            {/* Hidden tabs */}
            <Tab
              $isActive={activeTab === 'videoCall'}
              onClick={() => handleTabChange('videoCall')}
              style={{ display: 'none' }}
            >
              <TabIcon>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 7l-7 5 7 5V7z"></path>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                </svg>
              </TabIcon>
              <TabText>Video Call</TabText>
            </Tab>
            <Tab
              $isActive={activeTab === 'whiteBoard'}
              onClick={() => handleTabChange('whiteBoard')}
              style={{ display: 'none' }}
            >
              <TabIcon>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <path d="M9 9h6v6H9z"></path>
                  <path d="M9 21v-6"></path>
                  <path d="M21 9h-6"></path>
                </svg>
              </TabIcon>
              <TabText>WhiteBoard</TabText>
            </Tab>
          </TabsContainer>

          <TabContent>
            <TabPanel $isActive={activeTab === 'caseDashboard'}>
              {renderCaseDashboardTab()}
            </TabPanel>
            <TabPanel $isActive={activeTab === 'caseFile'}>
              {renderCaseFileTab()}
            </TabPanel>
            <TabPanel $isActive={activeTab === 'documentCenter'}>
              <UserFormsPanel
                key={selectedConversation?._id || selectedConversation?.threadId || 'no-user'}
                selectedUser={selectedConversation}
                threadId={selectedConversation?._id || selectedConversation?.threadId}
              />
            </TabPanel>
            <TabPanel $isActive={activeTab === 'clientData'}>
              <ClientProfilePanel
                key={`profile-${selectedConversation?._id || selectedConversation?.threadId || 'no-user'}`}
                selectedUser={selectedConversation}
              />
            </TabPanel>
            <TabPanel $isActive={activeTab === 'conversation'}>
              {renderConversationTab()}
            </TabPanel>
            <TabPanel $isActive={activeTab === 'history'}>
              {renderHistoryTab()}
            </TabPanel>
            <TabPanel $isActive={activeTab === 'videoCall'}>
              {renderVideoCallTab()}
            </TabPanel>
            <TabPanel $isActive={activeTab === 'whiteBoard'}>
              {renderWhiteBoardTab()}
            </TabPanel>
          </TabContent>
        </LeftPanel>

        <RightPanel>
          {/* Dynamic header based on active tab - only show for observer and macros */}
          {(rightPanelConfig.mode === 'observer' || rightPanelConfig.mode === 'macros') && (
            <PanelHeader>
              <img src={rightPanelConfig.icon} alt={rightPanelConfig.title} />
              <span>{rightPanelConfig.title}</span>
            </PanelHeader>
          )}

          <div style={{ flex: 1, overflow: 'hidden' }}>
            {renderRightPanelContent()}
          </div>
        </RightPanel>
      </MainContent>
    </PageWrapper>
  );
};

// Main component wrapped with provider
const WorkspacePage = () => {
  return (
    <WorkspaceAssistantProvider>
      <WorkspacePageContent />
    </WorkspaceAssistantProvider>
  );
};

export default WorkspacePage;
